import * as React from 'react';
import { useElementScrollRestoration } from '@tanstack/react-router';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

type UseWindowListVirtualizerOptions = {
    count: number;
    /** Fixed row height in px. */
    rowHeight: number;
    overscan?: number;
};

/**
 * Window-scrolled virtualizer for a fixed-row-height list that sits partway
 * down the page.
 *
 * - The virtualizer stays disabled until the list's page offset has been
 *   measured, so `scrollMargin` is correct when it first attaches. (Changing
 *   scrollMargin after attach makes it re-anchor and scroll the window to the
 *   top of the list.)
 * - `initialOffset` comes from the router's saved scroll position, so browser
 *   back/forward restores where the user was instead of being overwritten.
 * - `listHeight` is derived from the row count rather than the virtualizer, so
 *   the page is full height from the first render and the router's restore
 *   isn't clamped while the virtualizer is still disabled.
 *
 * Attach `listRef` to the list container and give it `height: listHeight`.
 */
export const useWindowListVirtualizer = ({ count, rowHeight, overscan = 8 }: UseWindowListVirtualizerOptions) => {
    const scrollEntry = useElementScrollRestoration({ getElement: () => window });

    const [listOffset, setListOffset] = React.useState<number | null>(null);
    // Keep the last measured offset when the list unmounts (e.g. while a new
    // sort order loads) so a remount doesn't change scrollMargin.
    const listRef = React.useCallback((node: HTMLElement | null) => {
        if (node) setListOffset(node.offsetTop);
    }, []);

    const virtualizer = useWindowVirtualizer({
        count,
        estimateSize: () => rowHeight,
        overscan,
        enabled: listOffset !== null,
        scrollMargin: listOffset ?? 0,
        initialOffset: scrollEntry?.scrollY ?? 0,
    });

    return { listRef, virtualizer, listHeight: count * rowHeight };
};
